import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import Swal from 'sweetalert2';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { localStorageKeys } from '../../utils/constants';
import './UploadMenuImage.css';

const API_BASE = process.env.REACT_APP_KITCHEN_BACKEND_API_URL;
const UPLOAD_API_URL = `${API_BASE}/UploadMenuImageToCloudinary`;
const FETCH_API_URL = `${API_BASE}/GetKitchenMenuImages`;
const DELETE_API_URL = `${API_BASE}/DeleteKitchenMenuImage`;
const MAX_FILES = 10;
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

/** Format ISO date string → "Jun 11, 2026" */
function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

/** Strip extension + sanitise into a Cloudinary-safe slug */
function deriveFileName(file) {
  return file.name
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
    .slice(0, 50) || 'menu-image';
}

const TABS = [
  { id: 'upload', title: 'Upload Menu Images', Icon: ImageOutlinedIcon },
  { id: 'schedule', title: 'Schedule Meeting', Icon: CalendarMonthOutlinedIcon },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getNextDays(n) {
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const today = new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return {
      label: i === 0 ? 'Today' : dayNames[d.getDay()],
      date: d.getDate(),
      month: monthNames[d.getMonth()],
      isoDate: d.toISOString().split('T')[0],
    };
  });
}

const TIME_SLOTS = ['9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];

// ─── Tab 1 — Upload Menu Images ───────────────────────────────────────────────

function MenuUploadTab() {
  // ── new-file selection state ──
  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadBanner, setUploadBanner] = useState(''); // post-upload success text
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // ── saved-images state ──
  const [savedImages, setSavedImages] = useState([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [savedError, setSavedError] = useState('');

  const kitchenName = useMemo(() => {
    try {
      const token = localStorage.getItem(localStorageKeys.TOKEN);
      if (!token) return '';
      const { kitchenName: kn } = jwtDecode(token);
      return kn || '';
    } catch {
      return '';
    }
  }, []);

  // ── fetch saved images ────────────────────────────────────────────
  const fetchSavedImages = useCallback(async () => {
    if (!kitchenName) return;
    setSavedLoading(true);
    setSavedError('');
    try {
      const params = new URLSearchParams({ kitchenName, imageType: 'menu' });
      const token = localStorage.getItem(localStorageKeys.TOKEN);
      const res = await fetch(`${FETCH_API_URL}?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (data.success) {
        setSavedImages(data.images || []);
      } else {
        setSavedError(data.message || 'Failed to load saved images.');
      }
    } catch {
      setSavedError('Network error while loading saved images.');
    } finally {
      setSavedLoading(false);
    }
  }, [kitchenName]);

  useEffect(() => {
    fetchSavedImages();
  }, [fetchSavedImages]);

  const handleDeleteImage = useCallback(
    (img) => {
      Swal.fire({
        icon: 'warning',
        iconColor: '#e65100',
        title: 'Delete menu image?',
        html: `
          <div style="font-size: 14px; color: #555; line-height: 1.6;">
            This will permanently remove this menu image from your store.
            <span style="color: #e53935; font-size: 12px; margin-top: 6px; display: block;">
              ⚠ This action cannot be undone.
            </span>
          </div>
        `,
        showCancelButton: true,
        confirmButtonColor: '#e53935',
        cancelButtonColor: '#9e9e9e',
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        focusCancel: true,
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: async () => {
          try {
            const token = localStorage.getItem(localStorageKeys.TOKEN);
            const res = await fetch(DELETE_API_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                kitchenName,
                publicId: img.publicId,
              }),
            });
            const data = await res.json();
            if (!data.success) {
              Swal.showValidationMessage(data.message || 'Failed to delete image.');
              return false;
            }
            return data;
          } catch {
            Swal.showValidationMessage('Network error. Please try again.');
            return false;
          }
        },
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          setSavedImages((prev) => prev.filter((item) => item.publicId !== img.publicId));
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: result.value.message || 'Menu image deleted successfully.',
            confirmButtonColor: '#2e7d32',
            confirmButtonText: 'OK',
          });
        }
      });
    },
    [kitchenName]
  );

  // ── file selection helpers ────────────────────────────────────────
  const addFiles = useCallback(
    (rawFiles) => {
      setError('');
      setUploadBanner('');
      const incoming = Array.from(rawFiles);
      const validationErrors = [];

      const valid = incoming.filter((f) => {
        if (!ALLOWED_TYPES.includes(f.type)) {
          validationErrors.push(`"${f.name}" — only JPEG and PNG are allowed`);
          return false;
        }
        if (f.size > MAX_SIZE_BYTES) {
          validationErrors.push(`"${f.name}" — exceeds the 5 MB limit`);
          return false;
        }
        return true;
      });

      const remaining = MAX_FILES - files.length;
      if (valid.length > remaining) {
        validationErrors.push(`Maximum ${MAX_FILES} images allowed — only ${remaining} more can be added`);
        valid.splice(remaining);
      }

      if (validationErrors.length) setError(validationErrors.join('. '));

      const items = valid.map((f) => ({
        id: Math.random().toString(36).slice(2),
        file: f,
        previewUrl: URL.createObjectURL(f),
      }));

      setFiles((prev) => [...prev, ...items]);
    },
    [files.length]
  );

  const removeFile = useCallback((id) => {
    setFiles((prev) => {
      const item = prev.find((f) => f.id === id);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const clearSelection = useCallback(() => {
    files.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setFiles([]);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [files]);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  // ── upload ────────────────────────────────────────────────────────
  const handleUpload = useCallback(async () => {
    if (!files.length) return;
    setUploading(true);
    setError('');
    setUploadBanner('');

    try {
      const formData = new FormData();
      formData.append('kitchenName', kitchenName);

      const fileNames = files.map((item) => deriveFileName(item.file));

      if (files.length === 1) {
        formData.append('fileName', fileNames[0]);
        formData.append('imageType', 'menu');
      } else {
        formData.append('fileNames', JSON.stringify(fileNames));
        formData.append('imageTypes', JSON.stringify(files.map(() => 'menu')));
      }

      files.forEach((item) => formData.append('file', item.file));

      const token = localStorage.getItem(localStorageKeys.TOKEN);
      const res = await fetch(UPLOAD_API_URL, {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const data = await res.json();

      if (data.success) {
        setUploadBanner(
          `✅ ${data.message} · ${data.totalImages} total image${data.totalImages !== 1 ? 's' : ''} on file`
        );
        clearSelection();
        fetchSavedImages(); // refresh the saved gallery
      } else {
        setError(data.message || 'Upload failed. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Network error. Please check your connection and try again.');
    } finally {
      setUploading(false);
    }
  }, [files, kitchenName, clearSelection, fetchSavedImages]);

  return (
    <div className='umi-panel'>
      {/* ── Hero ── */}
      <div className='umi-hero'>
        <div className='umi-hero__badge'>
          <span className='umi-hero__dot' />
          Menu Images
        </div>
        <h2 className='umi-hero__title'>
          Upload Your <span className='umi-grad-blue'>Menu Images</span>
        </h2>
        <p className='umi-hero__sub'>
          Drop multiple photos of your menu — we'll display them to customers on your store page.
        </p>
      </div>

      {/* ── Upload success banner ── */}
      {uploadBanner && (
        <div className='umi-success-banner'>{uploadBanner}</div>
      )}

      {/* ── Drop zone ── */}
      <div
        className={['umi-drop-zone', isDragOver ? 'umi-drop-zone--active' : ''].filter(Boolean).join(' ')}
        role='button'
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
        }}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type='file'
          accept='image/jpeg,image/png'
          multiple
          className='umi-hidden'
          onChange={(e) => addFiles(e.target.files)}
        />
        <div className='umi-drop-prompt'>
          <div className='umi-drop-icon'>🖼️</div>
          <p className='umi-drop-title'>Drop images here or click to browse</p>
          <p className='umi-drop-hint'>JPEG, PNG only · max 5 MB each · up to 10 files</p>
          <button
            type='button'
            className='umi-browse-btn'
            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
          >
            Browse files
          </button>
        </div>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className='umi-error-banner'>
          <span className='umi-error-banner__icon'>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* ── New-file preview grid ── */}
      {files.length > 0 && (
        <div className='umi-preview-section'>
          <div className='umi-preview-header'>
            <span className='umi-preview-count'>
              {files.length} / {MAX_FILES} image{files.length !== 1 ? 's' : ''} selected
            </span>
            <button className='umi-clear-btn' onClick={clearSelection}>Clear all</button>
          </div>
          <div className='umi-grid'>
            {files.map((item) => (
              <div key={item.id} className='umi-grid-item'>
                <img src={item.previewUrl} alt={item.file.name} className='umi-grid-img' />
                <div className='umi-grid-overlay'>
                  <p className='umi-grid-name'>{item.file.name}</p>
                  <p className='umi-grid-size'>{formatBytes(item.file.size)}</p>
                </div>
                <button className='umi-grid-remove' onClick={() => removeFile(item.id)} aria-label='Remove'>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Upload CTA ── */}
      <button
        className='umi-cta-btn'
        disabled={!files.length || uploading}
        onClick={handleUpload}
      >
        {uploading ? (
          <><span className='umi-spinner' /> Uploading…</>
        ) : (
          <>📤 Upload {files.length > 0 ? `${files.length} image${files.length !== 1 ? 's' : ''}` : 'Images'}</>
        )}
      </button>

      {/* ── Saved images gallery ── */}
      <div className='umi-saved'>
        <div className='umi-saved__header'>
          <span className='umi-saved__title'>Your Menu Images</span>
          {!savedLoading && (
            <span className='umi-result__badge'>
              {savedImages.length} image{savedImages.length !== 1 ? 's' : ''}
            </span>
          )}
          <button
            className='umi-saved__refresh'
            onClick={fetchSavedImages}
            disabled={savedLoading}
            title='Refresh'
          >
            {savedLoading ? <span className='umi-spinner umi-spinner--dark' /> : '↻'}
          </button>
        </div>

        {savedError && (
          <div className='umi-error-banner' style={{ marginTop: 8 }}>
            <span className='umi-error-banner__icon'>⚠️</span>
            <span>{savedError}</span>
          </div>
        )}

        {savedLoading && !savedError && (
          <div className='umi-saved__skeleton-grid'>
            {[1, 2, 3, 4].map((n) => <div key={n} className='umi-saved__skeleton' />)}
          </div>
        )}

        {!savedLoading && !savedError && savedImages.length === 0 && (
          <div className='umi-saved__empty'>
            <span className='umi-saved__empty-icon'>🖼️</span>
            <p>No images uploaded yet</p>
            <span>Images you upload will appear here</span>
          </div>
        )}

        {!savedLoading && savedImages.length > 0 && (
          <div className='umi-grid'>
            {savedImages.map((img) => (
              <div key={img.publicId} className='umi-grid-item'>
                <img src={img.imageUrl} alt={img.publicId} className='umi-grid-img' />
                <div className='umi-grid-overlay'>
                  <p className='umi-grid-name'>{img.imageType}</p>
                  <p className='umi-grid-size'>{formatDate(img.uploadedAt)}</p>
                </div>
                <a
                  href={img.imageUrl}
                  target='_blank'
                  rel='noreferrer'
                  className='umi-grid-view'
                  title='Open full image'
                >
                  ↗
                </a>
                <button
                  type='button'
                  className='umi-grid-remove'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteImage(img);
                  }}
                  aria-label='Delete image'
                  title='Delete image'
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 2 — Schedule Meeting ─────────────────────────────────────────────────

function ScheduleTab() {
  const days = getNextDays(7);
  const [selectedDay, setSelectedDay] = useState(days[1].isoDate);
  const [selectedTime, setSelectedTime] = useState('');
  const [form, setForm] = useState({ name: '', email: '', note: '' });
  const [booked, setBooked] = useState(false);
  const [booking, setBooking] = useState(false);

  const handleBook = async () => {
    if (!selectedDay || !selectedTime || !form.name.trim() || !form.email.trim()) return;
    setBooking(true);
    await new Promise((res) => setTimeout(res, 1600));
    setBooking(false);
    setBooked(true);
  };

  if (booked) {
    const bookedDay = days.find((d) => d.isoDate === selectedDay);
    return (
      <div className='umi-booked'>
        <div className='umi-booked__icon'>🎉</div>
        <h3 className='umi-booked__title'>Meeting Booked!</h3>
        <p className='umi-booked__detail'>
          {bookedDay?.label} · {selectedDay} at {selectedTime}
        </p>
        <p className='umi-booked__sub'>
          We'll send a confirmation to <strong>{form.email}</strong>
        </p>
        <button
          className='umi-browse-btn'
          style={{ marginTop: 24 }}
          onClick={() => {
            setBooked(false);
            setSelectedTime('');
            setForm({ name: '', email: '', note: '' });
          }}
        >
          Book another
        </button>
      </div>
    );
  }

  const isFormValid =
    selectedDay && selectedTime && form.name.trim() && form.email.trim();

  return (
    <div className='umi-panel'>
      <div className='umi-hero'>
        <div className='umi-hero__badge umi-hero__badge--orange'>
          <span className='umi-hero__dot umi-hero__dot--orange' />
          Schedule a Meeting
        </div>
        <h2 className='umi-hero__title'>
          Book a <span className='umi-grad-orange'>Setup Call</span>
        </h2>
        <p className='umi-hero__sub'>
          Pick a date and time — our team will walk you through setting up your store.
        </p>
      </div>

      {/* Day picker */}
      <div className='umi-section-label'>Select a date</div>
      <div className='umi-day-row'>
        {days.map((d) => (
          <button
            key={d.isoDate}
            className={[
              'umi-day-btn',
              selectedDay === d.isoDate ? 'umi-day-btn--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              setSelectedDay(d.isoDate);
              setSelectedTime('');
            }}
          >
            <span className='umi-day-name'>{d.label}</span>
            <span className='umi-day-num'>{d.date}</span>
            <span className='umi-day-month'>{d.month}</span>
          </button>
        ))}
      </div>

      {/* Time slots */}
      <div className='umi-section-label' style={{ marginTop: 22 }}>
        Select a time
      </div>
      <div className='umi-time-grid'>
        {TIME_SLOTS.map((t) => (
          <button
            key={t}
            className={[
              'umi-time-btn',
              selectedTime === t ? 'umi-time-btn--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setSelectedTime(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Contact form */}
      <div className='umi-form-row'>
        <div className='umi-form-field'>
          <label className='umi-section-label' style={{ marginBottom: 0 }}>
            Your name
          </label>
          <input
            className='umi-form-input'
            placeholder='John Smith'
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
        </div>
        <div className='umi-form-field'>
          <label className='umi-section-label' style={{ marginBottom: 0 }}>
            Email address
          </label>
          <input
            className='umi-form-input'
            type='email'
            placeholder='john@example.com'
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          />
        </div>
      </div>

      <div className='umi-form-field' style={{ marginTop: 14 }}>
        <label className='umi-section-label' style={{ marginBottom: 6 }}>
          Note (optional)
        </label>
        <textarea
          className='umi-form-input umi-form-textarea'
          placeholder="Any questions or topics you'd like to cover…"
          value={form.note}
          onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
        />
      </div>

      <button
        className='umi-cta-btn umi-cta-btn--orange'
        disabled={!isFormValid || booking}
        onClick={handleBook}
      >
        {booking ? (
          <>
            <span className='umi-spinner' /> Booking…
          </>
        ) : (
          <>📅 Book Meeting{selectedTime ? ` at ${selectedTime}` : ''}</>
        )}
      </button>
    </div>
  );
}

// ─── Page shell ───────────────────────────────────────────────────────────────

const UploadMenuImage = () => {
  const [activeTab, setActiveTab] = useState('upload');

  return (
    <div className='umi-page'>
      <div className='umi-shell'>
        <aside className='umi-toc'>
          <div className='umi-toc-header'>Menu Setup</div>
          {TABS.map((tab) => {
            const { Icon } = tab;
            const isActive = activeTab === tab.id;
            return (
              <div
                key={tab.id}
                className={[
                  'umi-toc-link',
                  isActive ? 'umi-toc-link--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setActiveTab(tab.id)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setActiveTab(tab.id);
                }}
              >
                <Icon sx={{ fontSize: 15, flexShrink: 0, opacity: isActive ? 1 : 0.7 }} />
                {tab.title}
              </div>
            );
          })}
        </aside>

        <main className='umi-content'>
          {activeTab === 'upload' ? <MenuUploadTab /> : <ScheduleTab />}
        </main>
      </div>
    </div>
  );
};

export default UploadMenuImage;
